'use client';

import { useId, useState } from 'react';
import { CaretDown, Check } from '@/components/icons';
import { useAnchored } from './use-anchored';
import { usePopover } from './use-popover';
import styles from './select.module.css';
import { useT } from '@/lib/locale-context';

/**
 * Select - the design-system dropdown (replaces the vanilla `<select>`). A button
 * trigger + a hand-built popover listbox: tokenised styling, click-outside / Escape
 * to dismiss, full keyboard support (arrows, Home/End, Enter, type-to-open), and a
 * check on the selected option. Same popover pattern as the app's other menus, so it
 * looks and behaves like one library. Headless behaviour, no native control.
 */

export interface SelectOption {
  value: string;
  label: string;
  hint?: string; // optional trailing context (e.g. a unit)
  /**
   * A second line under the label in the list: what choosing this option gives
   * (a role's meaning). Quiet and wrapping, nothing on the closed control. It is
   * the option's accessible DESCRIPTION and not part of its name, the way the
   * kit `MenuItem.description` is, so `getByRole('option', { name })` reaches the
   * option by its word. `hint` stays the short trailing word beside the label.
   */
  description?: string;
  disabled?: boolean;
}

export function Select({
  value,
  options,
  onChange,
  ariaLabel,
  placeholder,
  disabled,
  size = 'md',
  align = 'start',
  className,
}: {
  value: string;
  options: SelectOption[];
  onChange: (value: string) => void;
  ariaLabel?: string;
  placeholder?: string;
  disabled?: boolean;
  size?: 'sm' | 'md';
  align?: 'start' | 'end';
  className?: string;
}) {
  const t = useT();
  const uid = useId();
  const { open, setOpen, ref: wrapRef } = usePopover<HTMLDivElement>();
  const { anchorRef, floatingRef } = useAnchored<HTMLButtonElement, HTMLDivElement>(open, {
    align,
    matchWidth: true,
  });
  const [active, setActive] = useState(-1);
  const selected = options.find((o) => o.value === value) ?? null;

  function openMenu() {
    if (disabled) return;
    const i = options.findIndex((o) => o.value === value);
    setActive(i >= 0 ? i : step(options, -1, 1));
    setOpen(true);
  }
  function choose(opt: SelectOption) {
    if (opt.disabled) return;
    onChange(opt.value);
    setOpen(false);
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (disabled) return;
    if (!open) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) {
        e.preventDefault();
        openMenu();
      }
      return;
    }
    switch (e.key) {
      case 'Escape':
        // Handled: a popover holding this Select (use-popover.ts) leaves this
        // Esc alone, so it closes the list and not the panel around it.
        e.preventDefault();
        setOpen(false);
        break;
      case 'ArrowDown':
        e.preventDefault();
        setActive((i) => step(options, i, 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActive((i) => step(options, i, -1));
        break;
      case 'Home':
        e.preventDefault();
        setActive(step(options, -1, 1));
        break;
      case 'End':
        e.preventDefault();
        setActive(step(options, options.length, -1));
        break;
      case 'Enter':
      case ' ': {
        e.preventDefault();
        const o = options[active];
        if (o) choose(o);
        break;
      }
    }
  }

  return (
    <div className={className ? `${styles.wrap} ${className}` : styles.wrap} ref={wrapRef}>
      <button
        ref={anchorRef}
        type="button"
        className={styles.trigger}
        data-size={size}
        data-open={open || undefined}
        data-empty={!selected || undefined}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={() => (open ? setOpen(false) : openMenu())}
        onKeyDown={onKeyDown}
      >
        <span className={styles.value}>
          {selected ? selected.label : (placeholder ?? t('kit.select.placeholder'))}
        </span>
        <CaretDown className={styles.caret} aria-hidden />
      </button>

      {open && (
        <div ref={floatingRef} className={styles.menu} role="listbox" aria-label={ariaLabel}>
          {options.map((o, i) => {
            const labelId = `${uid}-${i}-label`;
            const hintId = `${uid}-${i}-hint`;
            const descId = `${uid}-${i}-desc`;
            return (
              <button
                key={o.value}
                type="button"
                role="option"
                aria-selected={o.value === value}
                aria-labelledby={
                  o.description ? (o.hint ? `${labelId} ${hintId}` : labelId) : undefined
                }
                aria-describedby={o.description ? descId : undefined}
                className={styles.option}
                data-active={i === active || undefined}
                disabled={o.disabled}
                onMouseEnter={() => setActive(i)}
                onClick={() => choose(o)}
              >
                {o.description ? (
                  <span className={styles.optText}>
                    <span className={styles.optLabel} id={labelId}>
                      {o.label}
                    </span>
                    <span className={styles.optDesc} id={descId}>
                      {o.description}
                    </span>
                  </span>
                ) : (
                  <span className={styles.optLabel}>{o.label}</span>
                )}
                {o.hint && (
                  <span className={styles.optHint} id={hintId}>
                    {o.hint}
                  </span>
                )}
                {o.value === value && <Check className={styles.check} aria-hidden />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** Next enabled option index from `from` in direction `dir`, wrapping. */
function step(options: SelectOption[], from: number, dir: 1 | -1): number {
  let i = from;
  for (let n = 0; n < options.length; n++) {
    i = (i + dir + options.length) % options.length;
    if (!options[i]!.disabled) return i;
  }
  return Math.max(0, from);
}
